import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react'

export type Theme = 'dark' | 'light' | 'system'
export type ResolvedTheme = 'dark' | 'light'

interface ThemeContextValue {
  /** Current theme setting ('dark' | 'light' | 'system') */
  theme: Theme
  /** The actual theme being applied ('dark' | 'light') */
  resolvedTheme: ResolvedTheme
  /** Set theme to a specific value */
  setTheme: (theme: Theme) => void
  /** Toggle between dark and light (ignores system) */
  toggleTheme: () => void
  /** Whether the system prefers dark mode */
  systemPrefersDark: boolean
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

const STORAGE_KEY = 'georanker-theme'

/**
 * Get the system color scheme preference
 */
function getSystemPreference(): ResolvedTheme {
  if (typeof window === 'undefined') return 'dark'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/**
 * Get initial theme from localStorage or default to 'system'
 */
function getInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'dark'

  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'dark' || stored === 'light' || stored === 'system') {
      return stored
    }
  } catch {
    // localStorage not available
  }

  return 'dark' // Default to dark (matches current design system)
}

/**
 * Apply theme class to document
 */
function applyTheme(resolvedTheme: ResolvedTheme) {
  if (typeof document === 'undefined') return

  const root = document.documentElement
  root.classList.remove('light', 'dark')
  root.classList.add(resolvedTheme)
}

interface ThemeProviderProps {
  /** Child components */
  children: ReactNode
  /** Default theme if none stored */
  defaultTheme?: Theme
  /** Force a specific theme (overrides all) */
  forcedTheme?: ResolvedTheme
}

/**
 * Theme provider component
 * Manages theme state, system preference detection, and persistence
 *
 * @example
 * <ThemeProvider>
 *   <App />
 * </ThemeProvider>
 */
export function ThemeProvider({
  children,
  defaultTheme = 'dark',
  forcedTheme,
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(defaultTheme)
  const [systemPrefersDark, setSystemPrefersDark] = useState(true)
  const [mounted, setMounted] = useState(false)

  // Initialize theme from storage on mount
  useEffect(() => {
    setThemeState(getInitialTheme())
    setSystemPrefersDark(getSystemPreference() === 'dark')
    setMounted(true)
  }, [])

  // Listen for system preference changes
  useEffect(() => {
    if (typeof window === 'undefined') return

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const handleChange = (e: MediaQueryListEvent) => {
      setSystemPrefersDark(e.matches)
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  // Calculate resolved theme
  const resolvedTheme: ResolvedTheme = forcedTheme
    ? forcedTheme
    : theme === 'system'
    ? (systemPrefersDark ? 'dark' : 'light')
    : theme

  // Apply theme to document
  useEffect(() => {
    if (!mounted) return
    applyTheme(resolvedTheme)
  }, [resolvedTheme, mounted])

  // Set theme and persist
  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme)

    try {
      localStorage.setItem(STORAGE_KEY, newTheme)
    } catch {
      // localStorage not available
    }
  }, [])

  // Toggle between dark and light
  const toggleTheme = useCallback(() => {
    const newTheme: Theme = resolvedTheme === 'dark' ? 'light' : 'dark'
    setTheme(newTheme)
  }, [resolvedTheme, setTheme])

  // Prevent flash of wrong theme
  if (!mounted) {
    return null
  }

  return (
    <ThemeContext.Provider
      value={{
        theme,
        resolvedTheme,
        setTheme,
        toggleTheme,
        systemPrefersDark,
      }}
    >
      {children}
    </ThemeContext.Provider>
  )
}

/**
 * Hook to access theme context
 *
 * @example
 * const { theme, toggleTheme, resolvedTheme } = useTheme()
 */
export function useTheme() {
  const context = useContext(ThemeContext)

  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }

  return context
}

export default ThemeProvider
